import { useCallback, useEffect, useRef } from 'react';

import { useRefreshToken } from '@/features/auth/api';
import { isEmpty } from 'lodash';

import {
    REFRESH_TOKEN_INTERVAL_CHECK_MS,
    REFRESH_TOKEN_THRESHOLD_MINUTES,
} from '@/lib/constants';

import { useActivity } from '@/hooks/useActivity';
import { useAuthData } from '@/hooks/useApiKey';

/**
 * Custom hook to handle automatic token refresh
 * Refreshes the access token when it's about to expire (2 minutes before) and if the user is active.
 * Checks periodically every minute
 *
 * Flows on each cycle:
 * 1. If the user is active and the access token is about to expire, it triggers a token refresh.
 * 2. If the user is inactive, it does not attempt to refresh the token.
 * 3. If the refresh is successful, it updates the auth data with the new tokens.
 * 4. If the refresh fails (access_token expires due to inactivity or refresh_token exceeds session lifetime), BE returns a 401 and the user is logged out
 *
 * Caveat that an inactive user would only be logged out on their next action after token expiry as the refresh is only attempted when the user is active.
 * This should be fine as other routes should throw 401s at this point
 */
export function useTokenRefresh() {
    const { accessTokenExpiresAt, refreshToken, setAuthData } = useAuthData();
    const [isUserActive] = useActivity();
    const refreshTokenQuery = useRefreshToken();

    const handleRefreshToken = useCallback(() => {
        refreshTokenQuery.mutate(refreshToken, {
            onSuccess: (data) => {
                setAuthData(data);
            },
        });
    }, [refreshTokenQuery, refreshToken, setAuthData]);

    const handleRefreshTokenRef = useRef(handleRefreshToken);

    useEffect(() => {
        handleRefreshTokenRef.current = handleRefreshToken;
    }, [handleRefreshToken]);

    useEffect(() => {
        const interval = setInterval(() => {
            const isWithinRefreshThreshold =
                Date.now() >=
                new Date(accessTokenExpiresAt).getTime() -
                    REFRESH_TOKEN_THRESHOLD_MINUTES * 60 * 1000;
            if (
                isUserActive() &&
                !isEmpty(accessTokenExpiresAt) &&
                isWithinRefreshThreshold
            ) {
                handleRefreshTokenRef.current();
            }
        }, REFRESH_TOKEN_INTERVAL_CHECK_MS);

        return () => {
            clearInterval(interval);
        };
    }, [accessTokenExpiresAt, isUserActive]);
}
